/**
 * [Phase 2d Stripe - Session B, founder-locked redlines]
 * AdminUserManagement - `/admin/users`
 *
 * User list with the three canonical access toggles per row:
 *   • has_auction_access
 *   • has_draft_analytics
 *   • has_season_analytics
 *
 * Search bar filters by name OR email (server-side via ?search=).
 * Each toggle is a switch that flips on/off via
 *   PATCH /api/admin/users/{user_id}/entitlements.
 *
 * Toggle source indicator next to the switch:
 *   • "paid"          - granted by a real Stripe payment
 *   • "admin_grant"   - granted by an earlier admin action (comp)
 *   • "admin_bypass"  - user is admin role (no toggle, always-on)
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, ShieldCheck } from "lucide-react";
import { useAuth, authHeaders } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SourceBadge({ source }) {
  if (!source) return null;
  const label = {
    paid: "paid",
    admin_grant: "comp",
    admin_bypass: "admin",
  }[source] || source;
  const color = {
    paid: "text-emerald-400 border-emerald-400/40",
    admin_grant: "text-amber-400 border-amber-400/40",
    admin_bypass: "text-sky-400 border-sky-400/40",
  }[source] || "text-white/50 border-white/20";
  return (
    <span
      data-testid={`toggle-source-${source}`}
      className={`ml-2 text-[9px] uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${color}`}
    >
      {label}
    </span>
  );
}

function Toggle({ value, source, disabled, onChange, testid }) {
  return (
    <div className="flex items-center">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        data-testid={testid}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors " +
          (disabled ? "opacity-50 cursor-not-allowed " : "cursor-pointer ") +
          (value ? "bg-emerald-500" : "bg-white/15")
        }
      >
        <span
          className={
            "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform " +
            (value ? "translate-x-4" : "translate-x-1")
          }
        />
      </button>
      <SourceBadge source={source} />
    </div>
  );
}

export default function AdminUserManagement() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flipping, setFlipping] = useState({}); // {`${userId}-${field}`: true}

  const fetchUsers = useCallback(async (q) => {
    setLoading(true);
    setError("");
    try {
      const params = q ? { search: q, limit: 200 } : { limit: 200 };
      const r = await axios.get(`${API}/admin/users`, {
        params, headers: authHeaders(),
      });
      setUsers(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers("");
  }, [fetchUsers]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => fetchUsers(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchUsers]);

  const flip = async (u, field, next) => {
    const key = `${u.id}-${field}`;
    setFlipping((m) => ({ ...m, [key]: true }));
    try {
      const r = await axios.patch(
        `${API}/admin/users/${u.id}/entitlements`,
        { [field]: next },
        { headers: authHeaders() },
      );
      const updated = r.data;
      setUsers((prev) =>
        prev.map((row) =>
          row.id === u.id
            ? {
                ...row,
                has_auction_access:     updated.has_auction_access,
                has_draft_analytics:    updated.has_draft_analytics,
                has_season_analytics:   updated.has_season_analytics,
                // Toggle source after flip: derived from the row state.
                // Server returns nothing about source on PATCH, so we mark
                // the changed toggle as 'admin_grant' when ON and clear
                // when OFF (paid rows stay paid).
                ...(field === "has_auction_access"
                  ? { auction_source: next
                      ? (row.auction_source === "paid" ? "paid" : "admin_grant")
                      : (row.auction_source === "paid" ? "paid" : null) }
                  : {}),
                ...(field === "has_draft_analytics"
                  ? { draft_analytics_source: next
                      ? (row.draft_analytics_source === "paid" ? "paid" : "admin_grant")
                      : (row.draft_analytics_source === "paid" ? "paid" : null) }
                  : {}),
                ...(field === "has_season_analytics"
                  ? { season_analytics_source: next
                      ? (row.season_analytics_source === "paid" ? "paid" : "admin_grant")
                      : (row.season_analytics_source === "paid" ? "paid" : null) }
                  : {}),
              }
            : row,
        ),
      );
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Toggle failed");
    } finally {
      setFlipping((m) => {
        const { [key]: _, ...rest } = m;
        return rest;
      });
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center">
        <p className="font-body opacity-70">Admin only.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page" className="min-h-screen bg-[#111111] text-[#F5F5F5] px-6 py-8">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav("/admin")}
              data-testid="admin-users-back"
              className="p-2 rounded-md hover:bg-white/5"
              aria-label="Back to admin dashboard"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="font-display font-black text-xs uppercase tracking-widest text-[#FF6B00]">User Management</p>
              <h1 className="font-display font-black text-2xl">Entitlement toggles</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs opacity-70">
            <ShieldCheck size={14} />
            Admin-only · paid rows are not revoked here
          </div>
        </header>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-2.5 opacity-50" />
          <input
            data-testid="admin-users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-[#1C1C1C] border border-[#252525] outline-none focus:border-[#FF6B00] font-body text-sm"
          />
        </div>

        {error && (
          <p data-testid="admin-users-error" className="font-body text-sm text-red-400 mb-4">{error}</p>
        )}

        {/* Table */}
        <div className="rounded-xl border border-[#252525] overflow-hidden">
          <table className="w-full" data-testid="admin-users-table">
            <thead className="bg-[#1C1C1C] text-[11px] uppercase tracking-wider font-body opacity-70">
              <tr>
                <th className="text-left px-4 py-2.5">User</th>
                <th className="text-left px-4 py-2.5">Created</th>
                <th className="text-left px-4 py-2.5">Auction</th>
                <th className="text-left px-4 py-2.5">Draft Analytics</th>
                <th className="text-left px-4 py-2.5">Season Analytics</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-6 text-center opacity-60 font-body text-sm">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center opacity-60 font-body text-sm">No users match.</td></tr>
              )}
              {!loading && users.map((u) => {
                const isAdmin = u.role === "admin";
                return (
                  <tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    className="border-t border-[#252525] hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 align-middle">
                      <div className="font-display font-black text-sm">{u.first_name} {u.last_name}</div>
                      <div className="font-body text-xs opacity-60">{u.email}</div>
                      {isAdmin && (
                        <span className="inline-block mt-0.5 text-[9px] uppercase tracking-widest text-sky-400 border border-sky-400/40 rounded-sm px-1.5 py-0.5">admin</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-middle text-xs opacity-70 font-body">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <Toggle
                        value={u.has_auction_access}
                        source={u.auction_source}
                        disabled={isAdmin || flipping[`${u.id}-has_auction_access`]}
                        onChange={(next) => flip(u, "has_auction_access", next)}
                        testid={`toggle-auction-${u.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <Toggle
                        value={u.has_draft_analytics}
                        source={u.draft_analytics_source}
                        disabled={isAdmin || flipping[`${u.id}-has_draft_analytics`]}
                        onChange={(next) => flip(u, "has_draft_analytics", next)}
                        testid={`toggle-draft-${u.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <Toggle
                        value={u.has_season_analytics}
                        source={u.season_analytics_source}
                        disabled={isAdmin || flipping[`${u.id}-has_season_analytics`]}
                        onChange={(next) => flip(u, "has_season_analytics", next)}
                        testid={`toggle-season-${u.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="font-body text-[11px] opacity-50 mt-4">
          Toggling ON grants access immediately (no payment required). Toggling OFF revokes admin-granted access only - Stripe-paid entitlements remain. Admin users always bypass all checks.
        </p>
      </div>
    </div>
  );
}
